import { useState } from "react";
import { Loader2, FolderOpen } from "lucide-react";
import { track } from "jiffy-analytics";

import type { Side } from "@design-lab/shared";
import { useNotification } from "@design-lab/shared/ui/notification";
import { useDebounce } from "@design-lab/shared/hooks/useDebounce";
import { useArtwork, useArtworkImage } from "@design-lab/entities/artwork";
import { useInfiniteScroll } from "@design-lab/shared/hooks/useInfiniteScroll";
import { useCommunityImages } from "../../api/useCommunityImages";
import { useArtworkSelection } from "../../model/useArtworkSelection";
import { MENU_IDS } from "../../model/state";
import { uploadCommunityImageToDesignLab } from "../../services";
import { CommunityImageGrid } from "./CommunityImageGrid";
import { SearchBar, type SortOption } from "./SearchBar";

type CommunityArtContentProps = {
  currentSide: Side;
};

export function CommunityArtContent({ currentSide }: CommunityArtContentProps) {
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState("newest");
  const [uploadState, setUploadState] = useState<{
    front: { imageId?: string; startTime?: number };
    back: { imageId?: string; startTime?: number };
  }>({
    front: {},
    back: {}
  });

  const debouncedSearch = useDebounce(searchInput, 500);

  const isSearchPending = searchInput !== debouncedSearch && searchInput.trim() !== "";

  const communityImagesOptions = {
    sortBy: sort,
    ...(debouncedSearch.trim() && { search: debouncedSearch.trim() })
  };

  const { communityImages, loading, isLoadingMore, hasMore, error, loadMore } =
    useCommunityImages(communityImagesOptions);

  const { setSelection, getActiveSelection, clearSelection } = useArtworkSelection();
  const { deleteArtwork, uploadArtwork } = useArtworkImage();
  const { getMachineBySide } = useArtwork();
  const { error: notifyError } = useNotification();

  const activeSelection = getActiveSelection(currentSide);
  const machine = getMachineBySide(currentSide);

  const enableInfiniteScroll = !isSearchPending && !loading;

  const { targetRef } = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    enabled: enableInfiniteScroll
  });

  const handleSearchChange = (value: string) => setSearchInput(value);
  const handleSortChange = (value: string) => setSort(value);

  const sortOptions: SortOption[] = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" }
  ];

  const handleImageSelect = async (imageId: string, imageUrl: string, imageName: string) => {
    if (uploadState[currentSide].imageId) {
      return;
    }

    if (
      activeSelection.activeSource === MENU_IDS.COMMUNITY_ART &&
      activeSelection.selectedId === parseInt(imageId, 10)
    ) {
      return;
    }

    track("community_art_selected", {
      page: "design_lab",
      id: parseInt(imageId, 10)
    });

    setUploadState(prev => ({
      ...prev,
      [currentSide]: { imageId, startTime: Date.now() }
    }));

    try {
      const existingArtworkId = machine.state.context.artworkId;
      if (existingArtworkId) {
        await deleteArtwork(currentSide, existingArtworkId.toString());
        clearSelection(currentSide);
      }

      setSelection(currentSide, MENU_IDS.COMMUNITY_ART, parseInt(imageId, 10));

      await uploadCommunityImageToDesignLab({
        imageUrl,
        imageName,
        uploadArtwork: file =>
          uploadArtwork({
            file,
            side: currentSide,
            source: "upload",
            communityImageId: imageId
          })
      });
    } catch (uploadError) {
      window.Honeybadger?.notify("Failed to upload community image", {
        params: {
          error: uploadError instanceof Error ? uploadError.message : String(uploadError),
          error_object: uploadError instanceof Error ? uploadError.toString() : String(uploadError),
          stack_trace: uploadError instanceof Error ? uploadError.stack : null,
          component: "CommunityArtContent",
          action: "handleImageUpload",
          imageId,
          side: currentSide
        }
      });
      clearSelection(currentSide);
      notifyError({
        title: "Upload Failed",
        description:
          uploadError instanceof Error
            ? uploadError.message
            : "Failed to upload community image. Please try again."
      });
    } finally {
      setUploadState(prev => ({
        ...prev,
        [currentSide]: {}
      }));
    }
  };

  return (
    <div className="flex h-full flex-1 flex-col gap-4 overflow-hidden">
      <SearchBar
        searchValue={searchInput}
        onSearchChange={handleSearchChange}
        sortValue={sort}
        onSortChange={handleSortChange}
        sortOptions={sortOptions}
        searchPlaceholder="Search image names"
        isSearchPending={isSearchPending}
        searchTestId="community-art-search-input"
        sortTestId="community-art-sort-select-trigger"
        clearSearchTestId="community-art-clear-search"
        expandSearchTestId="community-art-expand-search"
        closeSearchTestId="community-art-close-search"
      />

      <div className="h-full overflow-y-auto">
        {loading && (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <Loader2 className="size-8 animate-spin" />
            <p className="text-sm text-gray-600">Loading community images...</p>
          </div>
        )}

        {error && !loading && (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <FolderOpen className="size-8 text-gray-400" />
            <p className="text-sm text-gray-600">Failed to load community images</p>
          </div>
        )}

        {!loading && !error && communityImages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <FolderOpen className="size-8 text-gray-400" />
            <p className="text-sm text-gray-600">
              {debouncedSearch
                ? `No images found matching "${debouncedSearch}"`
                : "No community images available"}
            </p>
          </div>
        )}

        {!loading && !error && communityImages.length > 0 && (
          <CommunityImageGrid
            images={communityImages}
            currentSide={currentSide}
            uploadingImageId={uploadState[currentSide].imageId}
            uploadStartTime={uploadState[currentSide].startTime}
            onImageSelect={handleImageSelect}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            targetRef={targetRef}
          />
        )}
      </div>
    </div>
  );
}
